import React, { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { visibleCategories } from '@/config/adminNav';
import { Search, ChevronRight, Server, Loader2 } from 'lucide-react';

export default function PlatformConsoleHubPage() {
  const { user, hasPermission, permsLoaded } = useAuth();
  const [query, setQuery] = useState('');

  const categories = useMemo(
    () => visibleCategories(user, hasPermission, 'platform_console'),
    [user, hasPermission],
  );

  const q = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return null;
    const out = [];
    categories.forEach((cat) => {
      cat.items.forEach((it) => {
        if (it.label.toLowerCase().includes(q) || (it.description || '').toLowerCase().includes(q) || cat.label.toLowerCase().includes(q)) {
          out.push({ ...it, categoryLabel: cat.label, categoryId: cat.id });
        }
      });
    });
    return out;
  }, [q, categories]);

  // Wait for permissions to hydrate before deciding access (avoids a flash
  // redirect for super admins whose perms load a beat after the route mounts).
  if (!permsLoaded) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="platform-console-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  // No platform capabilities → not a Cloverleaf operator. Keep the console
  // undiscoverable to customer users.
  if (categories.length === 0) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="platform-console-hub">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <Server className="w-6 h-6 text-emerald-400" strokeWidth={1.75} />
          <h1 className="text-3xl font-heading font-bold text-foreground tracking-tight">Platform Console</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Cloverleaf cross-customer operations — {categories.length} categories available for your role.
        </p>
      </div>

      <div className="relative mb-8 max-w-xl">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search platform functions…"
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-card/60 border border-border/40 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
          data-testid="platform-search-input"
        />
      </div>

      {searchResults ? (
        <div data-testid="platform-search-results">
          <p className="text-xs text-muted-foreground mb-3">{searchResults.length} result(s)</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {searchResults.map((it) => (
              <Link
                key={`${it.categoryId}-${it.label}`}
                to={it.future ? '#' : it.to}
                onClick={(e) => it.future && e.preventDefault()}
                className={`flex items-start gap-3 p-3.5 rounded-xl border border-border/40 bg-card/40 ${it.future ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/40 hover:bg-card'} transition-all`}
              >
                <it.icon className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" strokeWidth={1.75} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground flex items-center gap-2">
                    {it.label}
                    {it.future && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground">Soon</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{it.categoryLabel}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <>
          <h2 className="text-sm font-heading font-semibold text-foreground mb-3">Categories</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="platform-categories">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/platform-console/${cat.id}`}
                className="group bg-card/60 border border-border/40 rounded-2xl p-5 hover:border-primary/40 hover:bg-card transition-all duration-200"
                data-testid={`platform-category-${cat.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-11 h-11 rounded-xl bg-muted/40 flex items-center justify-center ${cat.accent}`}>
                    <cat.icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="text-base font-heading font-semibold text-foreground">{cat.label}</div>
                <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">{cat.description}</p>
                <div className="text-[11px] text-muted-foreground/70 mt-3">
                  {cat.items.filter((i) => !i.future).length} tools
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
